/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { Box, Flex, Button, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Grid, GridItem } from '@chakra-ui/react';
import TextFormField from '../../components/form/TextFormField';
import DateFormField from '../form/DateFormField';
import MultipleFileFormField from '../form/MultipleFileFormField';
import CheckFormField from '../form/CheckFormField';
import moment from 'moment';
import c from '../../constant';
import * as Yup from 'yup';

const Q_GET_CERTIFICATE = gql`
	query get($id: String!) {
		getCompanyCertificateById(id: $id) {
			id
			number
			name
			nameLatin
			managerName
			managerNameLatin
			validFrom
			validTo
			files
			streetNumber
			houseNumber
			geoManual
			isGeoManual
			province
			district
			commune
			village
			isPermanentLicense
		}
	}
`;

const M_UPDATE_CERTIFICATE = gql`
	mutation create(
		$files: JSON
		$geoLocation: String
		$isGeoManual: Boolean
		$geoManual: String
		$isPermanentLicense: Boolean
		$validTo: DateTime
		$validFrom: DateTime
		$name: String!
		$nameLatin: String
		$number: String
		$id: String!
		$managerName: String
		$managerNameLatin: String
	) {
		updateCompanyCertificateById(
			files: $files
			geoLocation: $geoLocation
			isGeoManual: $isGeoManual
			geoManual: $geoManual
			isPermanentLicense: $isPermanentLicense
			validTo: $validTo
			validFrom: $validFrom
			name: $name
			nameLatin: $nameLatin
			number: $number
			id: $id
			managerName: $managerName
			managerNameLatin: $managerNameLatin
		)
	}
`;

const validationSchema = Yup.object().shape({
	number: Yup.string().nullable(),
	name: Yup.string().required('required'),
	managerName: Yup.string().nullable(),
	managerNameLatin: Yup.string().nullable(),
	isPermanentLicense: Yup.boolean().nullable(),
	validFrom: Yup.string().nullable(),
	validTo: Yup.string().nullable(),
	files: Yup.array().of(
		Yup.object().shape({
			file: Yup.string(),
			tempFile: Yup.mixed().when('file', {
				is: (file) => (file ? true : false),
				then: Yup.string(),
				otherwise: Yup.string().required('required'),
			}),
		})
	),
});

function Create({ id = null, isInsuranceAgent = false, isOpen = false, onClose = () => null, callback = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const [loadCertificate, { data: getData }] = useLazyQuery(Q_GET_CERTIFICATE);
	const [updateCertificate, { loading, updateError, data: updateData }] = useMutation(M_UPDATE_CERTIFICATE);

	useEffect(() => {
		if (id) {
			const variables = { id };
			loadCertificate({ variables });
		}
	}, [id]);

	useEffect(() => {
		if (updateData) {
			toast({
				title: t('Company certificate updated successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
			onClose();
			callback('Company certificate updated successfully');
		}
	}, [updateData]);

	useEffect(() => {
		if (updateError) {
			updateError.graphQLErrors.map(({ message }) =>
				toast({
					title: t(message),
					status: 'error',
					isClosable: true,
					position: 'top-right',
				})
			);
		}
	}, [updateError]);

	const certificateDate = (input) => {
		if (input) {
			return moment(input).toDate();
		}
		return null;
	};

	const certificateGeolocation = (input) => {
		const geoLocation = {
			province: input?.province,
			district: input?.district,
			commune: input?.commune,
			village: input?.village,
			streetNumber: input?.streetNumber,
			houseNumber: input?.houseNumber,
		};
		return geoLocation;
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl">
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius} pb={4}>
				<ModalHeader>{t('Edit Company Certificate')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{getData?.getCompanyCertificateById && (
						<Formik
							key={getData?.getCompanyCertificateById?.id}
							validationSchema={validationSchema}
							initialValues={{
								number: getData?.getCompanyCertificateById?.number || '',
								name: getData?.getCompanyCertificateById?.name || '',
								nameLatin: getData?.getCompanyCertificateById?.nameLatin || '',
								managerName: getData?.getCompanyCertificateById?.managerName || '',
								managerNameLatin: getData?.getCompanyCertificateById?.managerNameLatin || '',
								geoLocation: certificateGeolocation(getData?.getCompanyCertificateById),
								isGeoManual: getData?.getCompanyCertificateById?.isGeoManual || false,
								geoManual: getData?.getCompanyCertificateById?.geoManual || '',
								isPermanentLicense: isInsuranceAgent ? true : getData?.getCompanyCertificateById?.isPermanentLicense,
								validFrom: certificateDate(getData?.getCompanyCertificateById?.validFrom),
								validTo: certificateDate(getData?.getCompanyCertificateById?.validTo),
								files: getData?.getCompanyCertificateById?.files || [],
							}}
							onSubmit={(values) => {
								const variables = {
									id,
									number: values?.number,
									name: values?.name,
									nameLatin: values?.nameLatin,
									managerName: values?.managerName,
									managerNameLatin: values?.managerNameLatin,
									geoLocation: values?.geoLocation ? JSON.stringify(values?.geoLocation) : null,
									isGeoManual: values?.isGeoManual,
									geoManual: values?.geoManual,
									isPermanentLicense: isInsuranceAgent ? true : values?.isPermanentLicense,
									validTo: values?.isPermanentLicense ? null : values?.validTo,
									validFrom: values?.isPermanentLicense ? null : values?.validFrom,
									files: values?.files,
								};
								updateCertificate({ variables });
							}}
						>
							{({ values, ...props }) => {
								return (
									<Form>
										<Box mt="3">
											<Grid templateColumns="repeat(1, 1fr)" gap={6}>
												<GridItem w="100%">
													<TextFormField label="Number" name="number" />
													<TextFormField label="Name" name="name" required />
													<TextFormField label="Latin Name" name="nameLatin" />
													<TextFormField label="Manager Name" name="managerName" />
													<TextFormField label="Manager Name Latin" name="managerNameLatin" />
													<MultipleFileFormField name="files" label="Attachment(s)" editing={true} />
													<CheckFormField
														{...props}
														label="Is Permanent License"
														name="isPermanentLicense"
														isDisabled={isInsuranceAgent}
													/>
													{!values?.isPermanentLicense && (
														<Grid templateColumns="repeat(2, 1fr)" gap={6}>
															<GridItem w="100%">
																<DateFormField
																	name="validFrom"
																	label="Valid From"
																	initialDate={getData?.getCompanyCertificateById?.validFrom}
																/>
															</GridItem>
															<GridItem w="100%">
																<DateFormField
																	name="validTo"
																	label="Valid To"
																	initialDate={getData?.getCompanyCertificateById?.validTo}
																/>
															</GridItem>
														</Grid>
													)}
												</GridItem>
											</Grid>
											<Flex textAlign="right" mt="6" justifyContent="space-between" alignItems="center" float="right">
												<Button
													id="login-btn"
													variant="solid"
													size="md"
													h="42px"
													w="128px"
													bg="primary.dark"
													_hover={{ bg: 'primary.dark' }}
													color="white"
													type="submit"
													isLoading={loading}
													loadingText={t('Edit')}
													spinnerPlacement="start"
												>
													{t('Save')}
												</Button>
											</Flex>
										</Box>
									</Form>
								);
							}}
						</Formik>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default Create;
